/// FOURJS_START_COPYRIGHT(D,2024)
/// Property of Four Js*
/// (c) Copyright Four Js 2024, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum(
  "TopMenuService",
  ["InitService", "MainContainerService", "NavigationButtonService"],
  /**
   * @param {gbc} context
   * @param {classes} cls
   */
  function (context, cls) {
    /**
     * TopMenu service
     * @namespace gbc.TopMenuService
     * @gbcService
     */
    class TopMenuService /** @lends gbc.TopMenuService */ {
      constructor() {
        /**
         * @type {string}
         */
        this._categoryName = "menu";
      }

      /**
       * Init service
       */
      init() {
        context.MainContainerService.when(context.constants.widgetEvents.floatingPanelDocked, () => {
          context.NavigationButtonService.hideCategory(this._categoryName);
        });
        context.MainContainerService.when(context.constants.widgetEvents.floatingPanelUndocked, () =>
          context.NavigationButtonService.showCategory(this._categoryName)
        );
      }

      /**
       * Add topmenu in proper container
       * @param {classes.NodeBase} topMenuNode
       */
      add(topMenuNode) {
        const uiNode = topMenuNode.getAncestor("UserInterface");
        const windowNode = topMenuNode.getAncestor("Window") || topMenuNode.getApplication().getVMWindow();
        const parentNode = topMenuNode.getParentNode() === uiNode ? uiNode : windowNode;

        if (topMenuNode.getWidget() instanceof cls.StartMenuWidget) {
          // topmenu node generates a startmenu widget when it's intended to go in sidebar
          this._addInSidebar(topMenuNode, parentNode);
        } else {
          this._addInWindow(topMenuNode, parentNode, uiNode);
        }
      }

      _addInSidebar(topMenuNode, parentNode) {
        if (!parentNode?.getWidget()) {
          return;
        }
        topMenuNode.getWidget().onActivateHandler = parentNode.getWidget().when(context.constants.widgetEvents.activate, () => {
          context.NavigationButtonService.addAction(
            this._categoryName,
            topMenuNode.getWidget().getUniqueIdentifier(),
            this._addInFloatingPanel.bind(this, topMenuNode.getWidget())
          );
          if (context.MainContainerService.isDockedPanelVisible() || context.MainContainerService.shouldAutoDockFloatingPanel()) {
            context.MainContainerService.addDockedPanelItems(topMenuNode.getWidget()?.getElement());
            context.MainContainerService.showDockedPanel();
          }
        });
        topMenuNode.getWidget().onDisableHandler = parentNode.getWidget().when(context.constants.widgetEvents.disable, () => {
          this._removeFromSideBar(topMenuNode);
        });
      }

      /**
       * Add topMenu in window
       * @param {classes.NodeBase} topMenuNode
       * @param {classes.NodeBase|classes.WindowNode} parentNode - can be the parentWindow or the userInterface
       * @param {classes.NodeBase} uiNode
       * @private
       */
      _addInWindow(topMenuNode, parentNode, uiNode) {
        if (!parentNode?.getWidget()) {
          return;
        }
        // classic topmenu rendering
        // only topmenu from modal is being added inside modal
        let containerNode = parentNode;
        if (parentNode !== uiNode && !parentNode.isModal()) {
          containerNode = uiNode;
        }
        const order = topMenuNode.getParentNode() === uiNode ? 1 : 2;
        if (parentNode.getWidget().addTopMenu) {
          parentNode.getWidget().addTopMenu(topMenuNode.getWidget(), order, containerNode.getWidget());
        }

        // Add in sidebar when application is focused
        // windownode of type modal seems to not have widget in some case...
        topMenuNode.getWidget().onActivateHandler = parentNode.getWidget().when(context.constants.widgetEvents.activate, () => {
          topMenuNode.getWidget().setHidden(false);
        });

        topMenuNode.getWidget().onDisableHandler = parentNode.getWidget().when(context.constants.widgetEvents.disable, () => {
          if (parentNode.getTag() === "Window") {
            topMenuNode.getWidget().setHidden(true);
          }
        });
      }

      /**
       * Remove topmenu from its container
       * @param {classes.NodeBase} topMenuNode
       */
      remove(topMenuNode) {
        if (topMenuNode.getWidget().onDisableHandler) {
          topMenuNode.getWidget().onDisableHandler();
          topMenuNode.getWidget().onDisableHandler = null;
        }
        if (topMenuNode.getWidget().onActivateHandler) {
          topMenuNode.getWidget().onActivateHandler();
          topMenuNode.getWidget().onActivateHandler = null;
        }
        this._removeFromSideBar(topMenuNode);

        const winNode = topMenuNode.getAncestor("Window");
        winNode?.getWidget()?.removeTopMenu(topMenuNode.getWidget());
      }

      /**
       * Completely remove topmenu item from sidebar (docked panel or floating panel depending of the context)
       * @param topMenuNode
       * @private
       */
      _removeFromSideBar(topMenuNode) {
        if (context.MainContainerService.isDockedPanelVisible()) {
          context.MainContainerService.removeDockedPanelItem(topMenuNode.getWidget()?.getElement());
          if (context.MainContainerService.isDockedPanelEmpty()) {
            context.MainContainerService.removeDockedPanelUnDockButton();
            context.MainContainerService._destroySplitter();
          }
        } else {
          context.MainContainerService.removeFloatingPanelItem(topMenuNode.getWidget()?.getElement());
          context.MainContainerService.removeFloatingPanelDockButton();
        }
        context.NavigationButtonService.removeAction(this._categoryName, topMenuNode.getWidget().getUniqueIdentifier());
      }

      /**
       * Add topmenu in floating panel of chromebar navigation button
       * @param widget
       * @private
       */
      _addInFloatingPanel(widget) {
        if (!cls.WindowHelper.isMobile()) {
          // don't add dock option on mobile
          context.MainContainerService.addFloatingPanelDockButton();
        }
        context.MainContainerService.addFloatingPanelItems(widget.getElement());
        context.MainContainerService.showFloatingPanel("left");
      }
    }

    gbc.TopMenuService = new TopMenuService();
    context.InitService.register(context.TopMenuService);
  }
);
